<template>
  <!-- 根节点添加@click.stop，不然在el-popover中，SearchBox的@change穿不过来 -->
  <div class="font-list-panel" @click.stop>
    <SearchBox block-width placeholder="搜索字体" @change="handleSearch" />
    <div class="font-tabs-container">
      <el-segmented
        v-model="tabKey"
        :options="options"
        class="font-tabs"
        @change="handleTabChange"
      />
    </div>
    <div v-if="fontList.length > 0" class="font-list-scroll-wrap">
      <ScrollBar :item-gap="5" @end-reached="loadMore">
        <FontListItem
          v-for="(item, index) in fontList"
          :key="item.fontFamily"
          v-model="fontList[index]"
          :selected="item.fontFamily === fontFamily"
          @click="handleSelectFont"
        ></FontListItem>
      </ScrollBar>
    </div>
    <div v-else class="empty-font-list">暂无数据</div>
  </div>
</template>

<script setup lang="ts">
  import SearchBox from '@/components/common/input/SearchBox.vue';
  import ScrollBar from '@/components/common/list/ScrollBar.vue';
  import FontListItem from '@/components/common/list/item/FontListItem.vue';
  import { fontManager } from '@/managers';
  import { useFontStore } from '@/store';
  import type { FontInfo } from '@/types';
  import { type ScrollbarDirection } from 'element-plus';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted, ref } from 'vue';

  const fontFamily = defineModel<string>();

  const options = [
    { label: '简体', value: 'cn' },
    { label: '繁体', value: 'zhcn' },
    { label: '英文', value: 'en' }
  ];
  const pageCount = 20;

  const fontStore = useFontStore();
  const { onlineFontList, zhcnOnlineFontList, enOnlineFontList } = storeToRefs(fontStore);

  const tabKey = ref('cn');
  const pageIndex = ref(0);

  const targetFontList = ref<FontInfo[]>([]);

  const fontList = computed(() => {
    let endIndex = (pageIndex.value + 1) * pageCount;

    if (endIndex > targetFontList.value.length) {
      endIndex = targetFontList.value.length;
    }

    return targetFontList.value.slice(0, endIndex);
  });

  function getTabList(key: string) {
    switch (key) {
      case 'zhcn':
        return zhcnOnlineFontList.value;
      case 'en':
        return enOnlineFontList.value;
      default:
        return onlineFontList.value;
    }
  }

  async function handleTabChange(key: string) {
    pageIndex.value = 0;

    await fontManager.loadOnlineFonts(key as 'cn' | 'zhcn' | 'en');

    targetFontList.value = getTabList(key);
  }

  function loadMore(direction: ScrollbarDirection) {
    if (direction !== 'bottom') return;

    if (pageIndex.value >= Math.ceil(targetFontList.value.length / pageCount)) return;

    pageIndex.value++;
  }

  function handleSearch(fontName: string) {
    pageIndex.value = 0;

    const targetList = getTabList(tabKey.value);
    if (!fontName) {
      targetFontList.value = targetList;
    } else {
      targetFontList.value = targetList.filter((font) =>
        font.label.toLowerCase().includes(fontName.toLowerCase())
      );
    }
  }

  onMounted(async () => {
    await fontManager.loadOnlineFonts(tabKey.value as 'cn' | 'zhcn' | 'en');
    targetFontList.value = getTabList(tabKey.value);
  });

  async function handleSelectFont(info: FontInfo) {
    if (info.type === 'online') {
      const flag = await fontManager.registerFont(info);
      if (!flag) return;
    }

    fontFamily.value = info.fontFamily;
  }
</script>

<style scoped>
  .font-list-panel {
    width: 100%;
    height: 300px;
    border-radius: 4px;
    padding: 15px;
    box-sizing: border-box;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .font-list-panel > :deep(.search-box-container) {
    flex-shrink: 0;
  }

  .font-tabs-container {
    width: 100%;
    flex-shrink: 0;
    height: 32px;
  }

  .font-tabs {
    width: 100%;
    height: 100%;
    --el-segmented-bg-color: #eff0f7;
    --el-segmented-item-selected-bg-color: #fff;
    --el-segmented-item-selected-color: #160211;
    --el-segmented-item-color: #160211;
    --el-segmented-border-radius: 16px;
    --el-segmented-item-border-radius: 12px;
    padding: 4px;
  }

  .font-tabs :deep(.el-segmented__item) {
    height: 24px;
    font-size: 13px;
    font-weight: 400;
  }

  .font-tabs :deep(.el-segmented__item-label) {
    font-size: 13px;
    line-height: 14px;
  }

  .font-list-scroll-wrap {
    flex: 1;
    min-height: 0;
    width: 100%;
  }

  .empty-font-list {
    width: 100%;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 13px;
  }
</style>
